/**
 * Greeting Flow - Login Welcome Message
 *
 * This flow runs on every login to greet the user.
 * It's independent of the onboarding flow - onboarding only runs when setup is incomplete.
 *
 * Once onboarding is complete, users still get a greeting when they log in,
 * but they don't see onboarding prompts again.
 */

import { AiPrompt } from './userflow';

export const greetingFlow = {
  id: 'greeting_flow',
  name: 'Login Greeting',
  description: 'Personalized greeting message when user logs in',

  definition: {
    flows: {
      greeting_flow: {
        id: 'greeting_flow',
        name: 'Greeting',
        description: 'Show personalized greeting',
        stageId: 'initial_check' as const,
        steps: [
          {
            order: 1,
            action: 'guide' as const,
            id: 'show_greeting',
            title: 'Welcome',
            description: 'Show personalized greeting message',
            guide: {
              promptId: 'login_greeting',
              format: 'text' as const,
              showOptions: false,
            },
            nextStep: undefined, // No next step - greeting ends here
          },
        ],
        exitConditions: [
          {
            type: 'completed' as const,
            action: 'close_assistant',
          },
        ],
      },
    },

    forms: {}, // No forms needed for greeting

    aiPrompts: {
      login_greeting: {
        id: 'login_greeting',
        context: 'User logs in',
        variants: [
          'Welcome back to ESGCaffe! 👋 Ready to continue your ESG journey today?',
          'Great to see you again! How can I assist you with your ESG initiatives today?',
          'Hello! Welcome back to ESGCaffe. What would you like to focus on today?',
          'Nice to see you! Your ESG dashboard is all set. What would you like to explore?',
          'Welcome! Glad you\'re back. Let\'s make some ESG progress together! 🌱',
          'Hey there! ESGCaffe is ready for you. What\'s next on your agenda?',
          'Welcome back! I hope your ESG team is having a productive day. How can I help?',
          'Great timing! Let\'s dive into your ESG data and insights.',
          'You\'re back! What ESG metrics would you like to review today?',
          'Welcome! Your ESG reports and dashboards are waiting. Ready to dive in?',
        ],
        followUp: {
          text: 'Is there anything specific you\'d like help with?',
          expectedResponses: [
            'yes',
            'no',
            'help me with',
            'show me',
            'I need',
            'I want',
          ],
        },
      } as AiPrompt,

      setup_complete_greeting: {
        id: 'setup_complete_greeting',
        context: 'User has completed onboarding',
        variants: [
          'Welcome back! Your team and roles are all set up. 🎉',
          'You\'re all configured! Let\'s get to work on your ESG data.',
          'Everything is ready to go. Your ESGCaffe workspace is configured. Let\'s dive in!',
          'All systems ready! Your ESG setup is complete. What\'s next?',
        ],
        followUp: undefined,
      } as AiPrompt,
    },
  },
};
